import FormatUtils from "./FormatUtils";
import { Sexes } from "../config/RaceConfigs";
import { InsuranceCompanies } from "../config/RegistrationConfigs";
import { formatCountryForDisplay } from "./RegistrationUtils";

/**
 * Metadata keys that are only captured during registration (read-only on this screen).
 * Order defines display order on the profile page.
 */
export const USER_PROFILE_METADATA_REGISTRATION_KEYS = ["sex", "nationality", "birthNumber", "birthdate"];

/**
 * Metadata keys locked after first non-empty write (mirrors b7-app-main/app/config/user-metadata-policy.js).
 */
export const IMMUTABLE_IDENTITY_METADATA_KEYS = USER_PROFILE_METADATA_REGISTRATION_KEYS;

/**
 * Fields synced via {@link RegistrationCalls.updateProfile} self-service path.
 * Keep in sync with b7-app-main/app/config/profile-sync-configs.js CONTACT_INFO_FIELD_NAMES.
 */
export const CONTACT_INFO_FIELD_NAMES = [
  "phone",
  "insuranceCompany",
  "club",
  "street",
  "streetNumber",
  "city",
  "zip",
  "country",
];

/**
 * Metadata keys the user may update outside registration (contact / merchandise sizing).
 * Order defines display order on the profile page.
 */
export const USER_PROFILE_METADATA_EDITABLE_KEYS = [
  "phone",
  "insuranceCompany",
  "club",
  "street",
  "streetNumber",
  "zip",
  "city",
  "country",
  "tshirtSize",
];

/**
 * Builds a registration-shaped object from account + metadata for contact edit forms.
 * @param {Object} user - App context user
 * @returns {Object}
 */
export function buildVirtualRegistrationFromUser(user) {
  const metadata = user?.metadata ?? {};
  return {
    firstName: user?.firstName ?? "",
    surname: user?.lastName ?? "",
    email: user?.username ?? "",
    sex: metadata.sex,
    nationality: metadata.nationality,
    birthNumber: metadata.birthNumber,
    birthdate: metadata.birthdate,
    insuranceCompany: metadata.insuranceCompany,
    club: metadata.club,
    phone: metadata.phone,
    street: metadata.street,
    streetNumber: metadata.streetNumber,
    city: metadata.city,
    zip: metadata.zip,
    country: metadata.country,
  };
}

/**
 * Whether a metadata value should be shown as a row (non-empty primitive / meaningful string).
 * @param {unknown} value - Raw metadata value
 * @returns {boolean}
 */
export function hasUserProfileMetadataValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
}

/**
 * Resolves Czech health insurer label from numeric code, or returns the code as string.
 * @param {string|number} code - Insurance company code (e.g. `"500"`)
 * @returns {string}
 */
function insuranceCompanyDisplay(code) {
  const str = String(code);
  const row = InsuranceCompanies.find((c) => c.code === str);
  return row?.label ?? str;
}

/**
 * Formats a single user.metadata entry for read-only profile display.
 * @param {string} key - Metadata property name
 * @param {unknown} value - Stored value
 * @returns {string}
 */
export function formatUserProfileMetadataValue(key, value) {
  if (!hasUserProfileMetadataValue(value)) return "";

  switch (key) {
    case "sex":
      return Sexes[value]?.name ?? String(value);
    case "nationality":
    case "country":
      return formatCountryForDisplay(value);
    case "birthdate": {
      return FormatUtils.formatDate(value);
    }
    case "insuranceCompany":
      return insuranceCompanyDisplay(value);
    default:
      return String(value);
  }
}
